// src/ryan/components/product-blocks/VideoDemoBlock.tsx
// Bloco de demonstração "Vídeo" — texto editável (label/headline/subheadline,
// mesmo padrão dos outros blocos) + um vídeo curto centralizado abaixo.
//
// SEM AUTOPLAY, de propósito: o vídeo nasce parado, com uma capa (thumbnail)
// e um botão de play grande — só carrega/toca o <video> depois do clique.
// Isso evita que a página "roube a atenção" de quem ainda está lendo o Hero
// quando a seção entra na tela, e dá sensação de controle pro usuário.
//
// A capa é opcional (posterUrl, upload manual do admin) — quando ausente,
// cai pro frame automático derivado da própria URL do vídeo via
// transformação do Cloudinary (getCloudinaryVideoPoster, abaixo). O frame
// automático é só um fallback "melhor que nada": um instante aleatório do
// vídeo raramente é uma boa capa, por isso o upload manual tem prioridade.

import React, { useState, useEffect, useRef } from 'react';
import { Play } from 'lucide-react';
import { DemoHeader } from './DemoBlockShared';
import './VideoDemoBlock.css';

// ── Tipos ──────────────────────────────────────────────────────────────

export interface VideoDemoData {
    label: string;
    headline: string;
    subheadline: string;
    videoUrl: string;
    videoPublicId: string;
    posterUrl: string;
    posterPublicId: string;
}

export const defaultVideoDemoData = (): VideoDemoData => ({
    label: '',
    headline: 'Conheça a plataforma por dentro',
    subheadline: 'Veja em menos de 1 minuto como funciona o ambiente de estudos e como as tutorias são organizadas.',
    videoUrl: '',
    videoPublicId: '',
    posterUrl: '',
    posterPublicId: '',
});

// Deriva a URL de uma miniatura (frame em ~1s, formato .jpg) a partir da URL
// de vídeo que o Cloudinary retorna no upload — sem chamada extra de API,
// só reescrevendo a própria URL (recurso nativo do Cloudinary: pedir uma
// derivação de imagem de um recurso de vídeo já existente).
export function getCloudinaryVideoPoster(videoUrl: string): string {
    if (!videoUrl) return '';
    const withOffset = videoUrl.replace('/upload/', '/upload/so_1/');
    return withOffset.replace(/\.[a-zA-Z0-9]+(\?.*)?$/, '.jpg$1');
}

// ── Componente ───────────────────────────────────────────────────────

interface VideoDemoBlockProps {
    data: VideoDemoData;
    onPlay?: () => void;
    onProgress?: (percent: number) => void;
}

const PROGRESS_MILESTONES = [25, 50, 75, 100];

const VideoDemoBlock: React.FC<VideoDemoBlockProps> = ({ data, onPlay, onProgress }) => {
    const wrapRef = useRef<HTMLDivElement>(null);
    const [visible, setVisible] = useState(false);
    const [playing, setPlaying] = useState(false);
    // Marcos já reportados nesta reprodução — não usa state (não precisa
    // re-renderizar por isso) e reseta sozinho a cada novo elemento <video>
    // montado (troca de playing false→true), já que timeupdate dispara com
    // muita frequência pra ser feito via state sem re-render desnecessário.
    const firedMilestonesRef = useRef<Set<number>>(new Set());

    useEffect(() => {
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    setVisible(true);
                    observer.disconnect();
                }
            },
            { threshold: 0.12 }
        );
        if (wrapRef.current) observer.observe(wrapRef.current);
        return () => observer.disconnect();
    }, []);

    // Sem vídeo cadastrado ainda (bloco habilitado mas admin não subiu nada)
    // — nunca renderiza um player quebrado ou uma seção só com texto solto.
    if (!data.videoUrl) return null;

    const poster = data.posterUrl || getCloudinaryVideoPoster(data.videoUrl);

    return (
        <div className="rpb-demo-wrap" ref={wrapRef}>
            <DemoHeader label={data.label} headline={data.headline} subheadline={data.subheadline} visible={visible} />

            <div className={`rpb-vid-player ${visible ? 'rpb-vid-player--visible' : ''}`}>
                {playing ? (
                    <video
                        className="rpb-vid-el"
                        src={data.videoUrl}
                        poster={poster}
                        controls
                        autoPlay
                        playsInline
                        onTimeUpdate={e => {
                            const el = e.currentTarget;
                            if (!el.duration) return;
                            const percent = Math.floor((el.currentTime / el.duration) * 100);
                            for (const m of PROGRESS_MILESTONES) {
                                if (percent >= m && !firedMilestonesRef.current.has(m)) {
                                    firedMilestonesRef.current.add(m);
                                    onProgress?.(m);
                                }
                            }
                        }}
                        onEnded={() => {
                            if (!firedMilestonesRef.current.has(100)) {
                                firedMilestonesRef.current.add(100);
                                onProgress?.(100);
                            }
                        }}
                    />
                ) : (
                    <button
                        type="button"
                        className="rpb-vid-poster-btn"
                        style={{ backgroundImage: poster ? `url(${poster})` : undefined }}
                        onClick={() => { setPlaying(true); onPlay?.(); }}
                        aria-label="Reproduzir vídeo"
                    >
                        <span className="rpb-vid-play-circle">
                            <Play size={26} fill="currentColor" strokeWidth={0} />
                        </span>
                    </button>
                )}
            </div>
        </div>
    );
};

export default VideoDemoBlock;
